// One stage group (e.g. "In progress · 6") on the steering board. Holds
// the group header (pip + label + note + count) and the rows.

import type { JSX } from "react";

import type { DashboardIssue, DashboardStage } from "@loom/shared/dashboard";

import { useT, type MessageKey } from "../../i18n/index.js";

import { SteerRow } from "./SteerRow";

interface StageMeta {
  readonly labelKey: MessageKey;
  readonly color: string;
  readonly noteKey: MessageKey;
}

export const STAGE_META: Record<DashboardStage, StageMeta> = {
  // Red here, grey on the per-issue surfaces: `dashboard/stage.ts` colours
  // needs_input with `--st-backlog`, this board with `--st-blocked`, because on
  // the steering board it is the group that needs the user. A product decision,
  // deliberately not unified with that map.
  needs_input: {
    labelKey: "dashboard.stage.needs_input.label",
    color: "var(--st-blocked)",
    noteKey: "dashboard.stage.needs_input.note",
  },
  planned: {
    labelKey: "dashboard.stage.planned.label",
    color: "var(--st-ready)",
    noteKey: "dashboard.stage.planned.note",
  },
  in_progress: {
    labelKey: "dashboard.stage.in_progress.label",
    color: "var(--st-progress)",
    noteKey: "dashboard.stage.in_progress.note",
  },
  review: {
    labelKey: "dashboard.stage.review.label",
    color: "var(--st-review)",
    noteKey: "dashboard.stage.review.note",
  },
  shipped: {
    labelKey: "dashboard.stage.shipped.label",
    color: "var(--st-done)",
    noteKey: "dashboard.stage.shipped.note",
  },
};

interface SteerGroupProps {
  readonly stage: DashboardStage;
  readonly issues: ReadonlyArray<DashboardIssue>;
  readonly onEdit: (issue: DashboardIssue) => void;
  readonly onChat: (issue: DashboardIssue) => void;
}

export const SteerGroup = ({
  stage,
  issues,
  onEdit,
  onChat,
}: SteerGroupProps): JSX.Element => {
  const t = useT();
  const meta = STAGE_META[stage];
  return (
    <div className="steer-group">
      <div className="steer-group-head">
        <span className="steer-pip" style={{ background: meta.color }} />
        <span className="steer-group-label">{t(meta.labelKey)}</span>
        <span className="steer-group-note">{t(meta.noteKey)}</span>
        <span className="steer-group-count">{issues.length}</span>
      </div>
      <div className="steer-rows">
        {issues.map((issue) => (
          <SteerRow
            key={issue.iid}
            issue={issue}
            onEdit={onEdit}
            onChat={onChat}
          />
        ))}
      </div>
    </div>
  );
};
